import type { AdminSummaryResponse, KpiMetric } from "@codway-dev/shared";
import { MetricAreaCard, type ChartTone } from "./MetricAreaCard";
import {
  formatCurrencyExact,
  type MetricFormatType,
} from "../../utils/formatters";
import {
  type ChartSeriesPoint,
  type DashboardChartMetricKey,
  displayProductPageConversionRate,
} from "../../services/analytics.transforms";

type SummaryKpiField = Exclude<
  keyof AdminSummaryResponse,
  "period" | "previous_period"
>;

const EMPTY_KPI: KpiMetric = {
  value: 0,
  previous_value: 0,
  change_pct: null,
  is_positive: false,
};

function summaryKpi(
  summary: AdminSummaryResponse,
  field: SummaryKpiField
): KpiMetric {
  const metric = summary[field];
  return typeof metric === "object" && metric !== null ? metric : EMPTY_KPI;
}

function pdpConversionSummary(kpi: KpiMetric): KpiMetric {
  const normalized = displayProductPageConversionRate(kpi.value);
  const value =
    normalized !== null ? normalized : kpi.value > 100 ? Number.NaN : kpi.value;
  return { ...kpi, value };
}

function resolveSummaryData(
  summary: AdminSummaryResponse,
  field: SummaryKpiField
): KpiMetric {
  const kpi = summaryKpi(summary, field);
  return field === "product_page_conversion_rate"
    ? pdpConversionSummary(kpi)
    : kpi;
}

function resolveChartData(
  chartSeriesByMetric: Record<DashboardChartMetricKey, ChartSeriesPoint[]>,
  dataKey: DashboardChartMetricKey
): ChartSeriesPoint[] {
  return chartSeriesByMetric[dataKey] ?? [];
}

type MetricSlotConfig = {
  title: string;
  summaryField: SummaryKpiField;
  dataKey: DashboardChartMetricKey;
  formatType: MetricFormatType;
  chartTone: ChartTone;
  chartHeight?: number;
  maxXAxisTicks: number;
  tooltipContent: string;
};

const PRIMARY_METRICS: readonly MetricSlotConfig[] = [
  {
    title: "Aperturas del formulario",
    summaryField: "impressions",
    dataKey: "impressions",
    formatType: "integer",
    chartTone: "blue",
    chartHeight: 190,
    maxXAxisTicks: 3,
    tooltipContent:
      "Número de veces que los clientes abrieron el formulario durante el periodo seleccionado.",
  },
  {
    title: "Pedidos",
    summaryField: "completed_orders",
    dataKey: "completed_orders",
    formatType: "integer",
    chartTone: "blue",
    chartHeight: 190,
    maxXAxisTicks: 3,
    tooltipContent:
      "Pedidos completados que se generaron a través del formulario.",
  },
  {
    title: "Conversión",
    summaryField: "conversion_rate",
    dataKey: "conversion_rate",
    formatType: "percent",
    chartTone: "orange",
    chartHeight: 190,
    maxXAxisTicks: 3,
    tooltipContent:
      "Porcentaje de aperturas del formulario que terminaron en un pedido completado.",
  },
];

const PRODUCT_PAGE_METRICS: readonly MetricSlotConfig[] = [
  {
    title: "Visitas a la página de producto",
    summaryField: "product_page_views",
    dataKey: "product_page_views",
    formatType: "integer",
    chartTone: "blue",
    chartHeight: 190,
    maxXAxisTicks: 3,
    tooltipContent:
      "Número de veces que un cliente entró a una página de producto.",
  },
  {
    title: "Conversión en página de producto",
    summaryField: "product_page_conversion_rate",
    dataKey: "product_page_conversion_rate",
    formatType: "percent_whole",
    chartTone: "orange",
    chartHeight: 190,
    maxXAxisTicks: 3,
    tooltipContent:
      "Porcentaje de visitas a la página de producto. Si la tasa está fuera del rango posible, se muestra como '-'.",
  },
];

const ECONOMIC_METRICS: readonly MetricSlotConfig[] = [
  {
    title: "Ingresos",
    summaryField: "total_revenue",
    dataKey: "total_revenue",
    formatType: "currency",
    chartTone: "blue",
    chartHeight: 228,
    maxXAxisTicks: 4,
    tooltipContent:
      "Ingresos totales generados por los pedidos completados a través del formulario.",
  },
  {
    title: "Ticket medio",
    summaryField: "avg_order_value",
    dataKey: "avg_order_value",
    formatType: "currency",
    chartTone: "blue",
    chartHeight: 228,
    maxXAxisTicks: 4,
    tooltipContent:
      "Valor promedio de cada pedido generado a través del formulario (ingresos divididos entre el número de pedidos).",
  },
];

export type AnalyticsDashboardChartsProps = {
  summary: AdminSummaryResponse;
  chartSeriesByMetric: Record<DashboardChartMetricKey, ChartSeriesPoint[]>;
  currencyCode: string;
  numberLocale: string;
};

export function AnalyticsDashboardCharts({
  summary,
  chartSeriesByMetric,
  currencyCode,
  numberLocale,
}: AnalyticsDashboardChartsProps) {
  const revenueFmt = formatCurrencyExact(
    summaryKpi(summary, "total_revenue").value,
    currencyCode,
    numberLocale
  );

  return (
    <>
      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-200"
      >
        <s-stack direction="block" gap="small-200">
          <strong
            style={{
              fontWeight: 700,
              color: "var(--p-color-text, #202223)",
            }}
          >
            Resumen del formulario
          </strong>
          <s-text color="subdued">
            Aquí puedes ver el rendimiento de tu formulario.
          </s-text>
        </s-stack>
      </s-box>

      <s-grid gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="base">
        {PRIMARY_METRICS.map((slot) => (
          <MetricAreaCard
            key={slot.summaryField}
            title={slot.title}
            summaryData={resolveSummaryData(summary, slot.summaryField)}
            chartData={resolveChartData(chartSeriesByMetric, slot.dataKey)}
            formatType={slot.formatType}
            chartTone={slot.chartTone}
            chartHeight={slot.chartHeight}
            maxXAxisTicks={slot.maxXAxisTicks}
            tooltipContent={slot.tooltipContent}
            currencyCode={currencyCode}
            numberLocale={numberLocale}
          />
        ))}
      </s-grid>

      <s-stack direction="block" gap="base">
        <s-text type="strong">Tráfico en página de producto</s-text>
        <s-grid gridTemplateColumns="1fr 1fr" gap="base">
          {PRODUCT_PAGE_METRICS.map((slot) => (
            <MetricAreaCard
              key={slot.summaryField}
              title={slot.title}
              summaryData={resolveSummaryData(summary, slot.summaryField)}
              chartData={resolveChartData(chartSeriesByMetric, slot.dataKey)}
              formatType={slot.formatType}
              chartTone={slot.chartTone}
              chartHeight={slot.chartHeight}
              maxXAxisTicks={slot.maxXAxisTicks}
              tooltipContent={slot.tooltipContent}
              currencyCode={currencyCode}
              numberLocale={numberLocale}
            />
          ))}
        </s-grid>
      </s-stack>

      <s-stack direction="block" gap="base">
        <s-text type="strong">Resultados económicos</s-text>
        <s-grid gridTemplateColumns="1fr 1fr" gap="base">
          {ECONOMIC_METRICS.map((slot) => (
            <MetricAreaCard
              key={slot.summaryField}
              title={slot.title}
              summaryData={resolveSummaryData(summary, slot.summaryField)}
              chartData={resolveChartData(chartSeriesByMetric, slot.dataKey)}
              formatType={slot.formatType}
              chartTone={slot.chartTone}
              chartHeight={slot.chartHeight}
              maxXAxisTicks={slot.maxXAxisTicks}
              tooltipContent={slot.tooltipContent}
              currencyCode={currencyCode}
              numberLocale={numberLocale}
            />
          ))}
        </s-grid>
      </s-stack>

      <s-box
        background="base"
        border="small subdued solid"
        borderRadius="large-100"
        padding="large-200"
      >
        <s-stack direction="block" gap="small-200" alignItems="center">
          <p
            style={{
              margin: 0,
              width: "100%",
              textAlign: "center",
              fontSize: "1.125rem",
              lineHeight: 1.45,
              fontWeight: 700,
              color: "var(--p-color-text, #202223)",
            }}
          >
            <strong
              style={{
                fontWeight: "inherit",
                fontStyle: "normal",
                color: "inherit",
              }}
            >
              ¡Felicitaciones! Has generado {revenueFmt} de ingresos con Codway.
            </strong>
          </p>
        </s-stack>
      </s-box>
    </>
  );
}
